import React, { useCallback, useEffect, useState  } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Translate } from 'app/services';
import { ActionButton } from '../../../../common'; 
import { useAuthState } from '../../../../../hooks/auth';
import { trackScorm, openWindow } from '../../../../../ScormTracker/ScormTracker';
import { IS_HOYA_THEME } from 'app/branding';
import { buildCoursePath } from 'app/utils/buildCoursePath';
import { useTranslations } from 'app/hooks/translations/useTranslations';
import { CourseUtils } from '../../../../../utils/';
import { PaperIcon } from '@nackle/paper';
import { useQuery } from '@apollo/react-hooks';
import { LXP_FILE_QUERY } from '../../../../../gql/queries/LXP_FILE_QUERY';
import { patchAssignment } from '../../../../../utils/patchAssignment';
import './styles.less';
import StatusIcon from '../../../../status/Status';

const useImperativeQuery = ( query ) => {
    const { refetch } = useQuery( query, { skip: true } );
      
    const imperativelyCallQuery = ( variables ) => {
      return refetch( variables );
    };
      
    return imperativelyCallQuery;
};

export const getFileType = ( mimetype ) => {
    const fileTypeSpecs = getFileTypeSpecs( mimetype );
    return fileTypeSpecs;
};

export const getFileTypeSpecs = ( mimeType ) => {

    const FILE_TYPE_SPECS = {
        SCORM: {
            key: 'SCORM',
            transKey: 'file.type.scorm',
            icon: <PaperIcon icon="FilePdf" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => [ 'application/zip', 'application/x-zip-compressed' ].includes( fileType )
        },
        PDF: {
            key: 'PDF',
            transKey: 'file.type.pdf',
            icon: <PaperIcon icon="FilePdf" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => [ 'application/pdf' ].includes( fileType )
        },
        WORD: {
            key: 'WORD',
            transKey: 'file.type.word',
            icon: <PaperIcon icon="FileWord" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => [ 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ].includes( fileType )
        },
        EXCEL: {
            key: 'EXCEL',
            transKey: 'file.type.excel',
            icon: <PaperIcon icon="FileExcel" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => [ 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ].includes( fileType )
        },
        PPT: {
            key: 'PPT',
            transKey: 'file.type.ppt',
            icon: <PaperIcon icon="FilePpt" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => [ 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ].includes( fileType )
        },
        IMAGE: {
            key: 'IMAGE',
            transKey: 'file.type.image',
            icon: <PaperIcon icon="FileImage" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => fileType?.startsWith( 'image/' )
        },
        VIDEO: {
            key: 'VIDEO',
            transKey: 'file.type.video',
            icon: <PaperIcon icon="PlayCircle" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => fileType?.startsWith( 'video/' )
        },
        AUDIO: {
            key: 'AUDIO',
            transKey: 'file.type.audio',
            icon: <PaperIcon icon="Audio" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => fileType?.startsWith( 'audio/' )
        },
        URL: {
            key: 'URL',
            transKey: 'file.type.url',
            icon: <PaperIcon icon="Global" collection="antd-outlined"/>,
            fileCheck: ( fileType ) => fileType?.startsWith( 'text/uri-list' )
        },
        _: {
            key: 'OTHER',
            transKey: 'file.type.other',
            icon: <PaperIcon icon="File" collection="antd-outlined"/>,
            fileCheck: () => true
        }
    };

    return Object.values( FILE_TYPE_SPECS ).find( f => f.fileCheck( mimeType ) );
};

export const LearningObjectItem = ( props ) => {
    const { locale } = useTranslations();
    const [ courseData ] = useState( props.courseData ); 
    const { claims: { family_name: lastName, given_name: firstName } } = useAuthState();
    const [ courseWindow, setCourseWindow ] = useState();

    const buildLaunchUrl = useCallback( ( path, localeCode ) => {
        
        console.log( 'Original Lesson URL: ' + path );
        
        let tempPath = path;
        
        // Uncomment one of these to test with a course locally 
        //return  'http://localhost:3021/scorm/test-bourne-course/';   //1.2
        //tempPath = 'http://localhost:3021/scorm/chameleon-12/index.html';
        //return  'http://localhost:3021/scorm/jay-cham-4-scorm-4/';   //1.2
        //tempPath = 'http://localhost:3021/scorm/golf1/shared/launchpage.html';
        //return tempPath = 'http://localhost:3021/scorm/mon-2004/index.html';
        
        // 2004 course
        //  tempPath = 'http://localhost:3021/scorm/chameleon-2004-4th-edition/index.html'; //2004 + cert
        //tempPath = 'http://localhost:3021/scorm/chameleon-2004-3rd-edition/index.html';
        
        //tempPath = 'http://localhost:3021/scorm/test-camelean-course/';
        //tempPath = 'http://localhost:3021/scorm/test-bourne-course/';
        //tempPath = 'http://localhost:3021/scorm/presentingwithimpact/index.html'; 
        
        // Rise 1.2
        //tempPath = 'http://localhost:3021/scorm/single-vision-rise/scormdriver/indexAPI.html';
        //tempPath = 'http://localhost:3021/scorm/rx-rise/scormdriver/indexAPI.html';
        //tempPath = 'http://localhost:3021/scorm/rise-hygine/scormdriver/indexAPI.html';

        const tempLocale =  CourseUtils.localeToChameleonLanguage( localeCode ); // SAVE THIS, WILL BE USED LATER localeCode === 'en-US' ? 'USA' : localeCode;
        console.log( 'Using locale for Chameleon: ', tempLocale );
        
        const modPath = tempPath.replace( /(http(s?):|)(^|\/\/)(.*?\/)/g, '/' );
        
        const url = `https://${ window.location.hostname }${ modPath }?language=${ tempLocale }&theme=${ IS_HOYA_THEME ? 'hoya' : 'seiko' }`;
        console.log( 'Modified Lesson URL:' +  url );
        
        return url;
    }, [] );

    const callQuery = useImperativeQuery( LXP_FILE_QUERY );

    const handleOnStartCouse = async () => { 
        props.onOpenCallback && props.onOpenCallback();
      
        let type = getFileType( props.lessonData?.mimeType ).key;
        if ( props.lessonData?.format === 'scorm' ) { 
            type = 'SCORM';
        }

        switch ( type ) { 
            case 'SCORM':
                trackScorm( courseData, props.lessonData, 
                    `${ lastName }, ${ firstName }`, `${ firstName }${ lastName }`,
                    CourseUtils.localeToChameleonLanguage( locale ) );
                const url =  buildLaunchUrl( buildCoursePath( props.lessonData?.url ), locale );
                setCourseWindow( openWindow( url, props.onUpdate ) );
                break;
            case 'URL':
                window.open( props.lessonData?.url, '_blank' );
                await setFileToCompleted();

                if ( props.onUpdate ) {
                    setTimeout( () => {
                        props.onUpdate();
                    }, 2000 );
                }
                break;
            default:
                if ( props.lessonData?.assets && props.lessonData.assets.length > 0 ) {
                    const asset = props.lessonData.assets[ 0 ];
                     
                    const { data, error } = await callQuery(  {
                        lxpFileID: asset.asset.lxpFileId 
                    } );
     
                    const interval = setInterval( async () => {
                        if ( data ) { 
                            clearInterval( interval );
                            window.open( data.lxpFile.node.signedUrl, '_blank' );
                            await setFileToCompleted();

                            if ( props.onUpdate ) {
                                setTimeout( () => {
                                    props.onUpdate();
                                }, 2000 );
                            }
                        }

                        if ( error ) {
                            clearInterval( interval );
                            // TODO show error notification
                        }
                    }, 50 );
                }
        }
    };

    const setFileToCompleted = async () => {
        await patchAssignment( props.courseId, {
            learningObjectID: props.learningObjectId,
            courseID: props.courseId,
            status: 'COMPLETED',
            score: 0
        } );
    };

    const closeCourseWindow = useCallback( () => {
        if ( courseWindow ) {
            courseWindow.close();
        }
    }, [ courseWindow ] );

    useEffect( () => {
        window.addEventListener( 'beforeunload', closeCourseWindow );
        
        return () => {
          window.removeEventListener( 'beforeunload', closeCourseWindow );
        };
      }, [ closeCourseWindow ] );
    
    const rootClasses = classnames(
        'course-details-level-item',
        'learning-object-item',
        { 'animate': Boolean( props.animate ) },
        { [ props.className ]: Boolean( props.className ) },
        { locked: props.locked }
    );

    function handleOnClick() {
        if ( props.locked ) {
            return;
        }
        handleOnStartCouse();
    }

    return (
        <div className={ rootClasses } style={ props.style }>
            <div className='overlay' onClick={ handleOnClick } />
            <span className="course-details-level-item-label _subtitle text-em">
                { props.label }
            </span>

            <div className="divider" />

            <div className="course-details-status">
                { props.locked ?
                    <span className="course-details-level-item-status-icon-container primary">
                        <PaperIcon collection='ultimate' icon='Lock4' />
                    </span> :
                    <StatusIcon status={ props.status } />
                
                }

                <ActionButton
                    className={ classnames( 'course-details-level-item-action-button learning-action _subtitle text-em', { locked: props.locked } ) }
                    important={ !props.isComplete }
                    disabled={ props.disabled }
                    onClick={ handleOnClick }
                >
                     
                     { props.locked ?
                        <Translate.Text value='learningObject.actions.locked'/> :
                        props.isComplete ?
                            <Translate.Text value="learningObject.actions.review" /> :
                            <Translate.Text value="learningObject.actions.launch" />
                    }
                </ActionButton>
            </div>
        </div>
    );
};

LearningObjectItem.defaultProps = {
    className: undefined,
    style: undefined,
    id: undefined,
    label: 'Unknown',
    isComplete: false,
    animate: true,
    onUpdate: undefined,
    disabled: false,
};

LearningObjectItem.propTypes = {
    className: PropTypes.string, 
    style: PropTypes.object,
    label: PropTypes.string,
    isComplete: PropTypes.bool,
    animate: PropTypes.bool,
    disabled: PropTypes.bool,
    locked: PropTypes.bool,
};
